import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics";

/**
 * Global scroll-depth tracker. Fires `scroll_75` once per page load
 * when the user scrolls past 75% of the document height.
 */
const ScrollDepthTracker = () => {
  useEffect(() => {
    let fired = false;
    let ticking = false;
    // Cache document height to avoid forcing a full layout on every
    // scroll frame. Refresh on resize and at most every 1s during scroll.
    let cachedTotal = document.documentElement.scrollHeight;
    let lastRefresh = 0;
    const check = () => {
      ticking = false;
      if (fired) return;
      const now = performance.now();
      if (now - lastRefresh > 1000) {
        cachedTotal = document.documentElement.scrollHeight;
        lastRefresh = now;
      }
      const scrolled = window.scrollY + window.innerHeight;
      const total = cachedTotal;
      if (total <= 0) return;
      if (scrolled / total >= 0.75) {
        fired = true;
        trackEvent("scroll_75", { page_path: window.location.pathname });
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onResize);
      }
    };
    const onScroll = () => {
      if (ticking || fired) return;
      ticking = true;
      window.requestAnimationFrame(check);
    };
    const onResize = () => {
      cachedTotal = document.documentElement.scrollHeight;
      lastRefresh = performance.now();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);
  return null;
};

export default ScrollDepthTracker;
